import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { SphereShareLink } from "@components/common/sphere-share-link";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useToast } from "@flat-ui";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { useSharedMarkupActions } from "@hooks/project-markups/use-shared-markups-actions";
import { SHARE_ANNOTATION_DIALOG_PROVIDER_PROPS } from "@pages/project-details/project-markups/markup-action-share-markup";
import { DeleteMarkupDialog } from "@pages/project-details/project-markups/markup-delete-dialog";
import { useAppDispatch } from "@store/store-helper";
import { updateSidebar } from "@store/ui/ui-slice";
import { AnnotationEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
/** Actions of the more options menu of a markup, shared by the table and the list of small screens */
export function useMarkupMoreOptions({
  markup
}) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    openToast
  } = useToast();
  const {
    createDialog
  } = useSphereDialog();
  const {
    markupHref
  } = useSharedMarkupActions({
    markup
  });
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const dispatch = useAppDispatch();
  const {
    hasPermissionToEditMarkup,
    projectId,
    updateIsSidePanelOpen,
    updateSelectedMarkup,
    updateActiveTab
  } = useMarkupContext();
  const {
    deleteMarkupFromProject
  } = useProjectMarkupUpdate({
    projectId
  });
  function shareMarkup() {
    trackEvent({
      name: AnnotationEvents.shareAnnotation,
      props: {
        location: "markup-action"
      }
    });

    // Early exit if there isn't a markup URL to share
    if (!markupHref) {
      return;
    }
    void createDialog(SHARE_ANNOTATION_DIALOG_PROVIDER_PROPS, <SphereShareLink title="Annotation link" url={markupHref} copyLogEventParams={{
      name: AnnotationEvents.copyShareAnnotationLink,
      props: {
        location: "markup-action"
      }
    }} />);
  }
  function openMarkup() {
    trackEvent({
      name: AnnotationEvents.openAnnotationInNewTab,
      props: {
        dataType: TableType.projectMarkups
      }
    });
    if (!markupHref) {
      return;
    }
    window.open(markupHref, "_blank")?.focus();
  }
  function viewDetails() {
    trackEvent({
      name: AnnotationEvents.viewAnnotationDetails,
      props: {
        dataType: TableType.projectMarkups
      }
    });
    updateIsSidePanelOpen(true);
    updateSelectedMarkup(markup);
    updateActiveTab("Info");
    dispatch(updateSidebar({
      isFullSize: false
    }));
  }
  async function removeMarkupFromProject() {
    setIsDeleting(true);
    trackEvent({
      name: AnnotationEvents.deleteAnnotation,
      props: {
        dataType: TableType.projectMarkups
      }
    });
    try {
      await dispatch(() => deleteMarkupFromProject(markup));
      openToast({
        title: "Annotation deleted."
      });
    } catch (error) {
      handleErrorWithToast({
        id: `Delete annotation-${Date.now().toString()}`,
        title: "Failed to delete the annotation.",
        error
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      updateIsSidePanelOpen(false);
      updateSelectedMarkup(undefined);
    }
  }
  return {
    openMarkup,
    viewDetails,
    shareMarkup,
    canDeleteMarkup: hasPermissionToEditMarkup,
    requestDeleteMarkup: () => setIsDeleteModalOpen(true),
    deleteDialog: isDeleteModalOpen ? <DeleteMarkupDialog dialogContent={{
      title: "Delete annotation?",
      description: "This will remove the annotation from your project."
    }} onClose={() => setIsDeleteModalOpen(false)} onConfirm={removeMarkupFromProject} isDeleting={isDeleting} /> : null
  };
}